import React from 'react';
import {
  PredictionOutcome,
  getOutcomeAccentColor,
  getOutcomeBorderColor,
  getOutcomeLabel,
  getOutcomeMutedTextColor,
} from '../utils/predictionOutcomeUtils';
import { formatPoints } from '../utils/scoreUtils';

interface ScoredMatchSummaryProps {
  prediction: {
    homeGoals: number;
    awayGoals: number;
    isBanker?: boolean;
    points?: number;
  };
  outcome: Extract<PredictionOutcome, 'exact' | 'correct' | 'wrong'>;
}

const outcomeBadgeClasses: Record<ScoredMatchSummaryProps['outcome'], string> = {
  exact: 'bg-amber-900/10 text-amber-950 dark:bg-black/20 dark:text-amber-950',
  correct: 'bg-green-900/10 text-green-900 dark:bg-black/20 dark:text-green-50',
  wrong: 'bg-red-900/10 text-red-900 dark:bg-black/20 dark:text-red-50',
};

const ScoredMatchSummary: React.FC<ScoredMatchSummaryProps> = ({ prediction, outcome }) => {
  const accentColor = getOutcomeAccentColor(outcome);
  const borderColor = getOutcomeBorderColor(outcome);
  const mutedColor = getOutcomeMutedTextColor(outcome);
  const label = getOutcomeLabel(outcome);
  const points = prediction.points ?? 0;

  return (
    <div className={`mt-5 pt-4 border-t ${borderColor}`}>
      <div className="flex flex-col items-center text-center gap-2 py-1">
        <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${mutedColor}`}>
          Your prediction
        </p>

        <div className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 ${accentColor}`}>
          <span className="text-4xl sm:text-[2.75rem] font-bold tabular-nums leading-none tracking-tight">
            {prediction.homeGoals}
            <span className="mx-2 font-light opacity-30">-</span>
            {prediction.awayGoals}
          </span>
          {prediction.isBanker && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-black/10 dark:bg-black/20">
              Banker
            </span>
          )}
        </div>

        <div
          className={`inline-flex items-center gap-2 mt-1 px-3 py-1 rounded-full text-xs font-semibold ${outcomeBadgeClasses[outcome]}`}
        >
          <span className="uppercase tracking-wide">{label}</span>
          <span className="opacity-35">·</span>
          <span className="tabular-nums">{formatPoints(points)}</span>
        </div>
      </div>
    </div>
  );
};

interface MissedMatchSummaryProps {
  className?: string;
}

export const MissedMatchSummary: React.FC<MissedMatchSummaryProps> = ({ className = '' }) => (
  <div className={`mt-6 pt-5 border-t border-gray-200 dark:border-gray-700 text-center ${className}`}>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No prediction made</p>
  </div>
);

interface PendingPredictionSummaryProps {
  prediction: {
    homeGoals: number;
    awayGoals: number;
    isBanker?: boolean;
  };
}

export const PendingPredictionSummary: React.FC<PendingPredictionSummaryProps> = ({ prediction }) => (
  <div className="mt-5 pt-4 border-t border-green-600/25 dark:border-green-500/30">
    <div className="flex flex-col items-center text-center gap-2 py-1">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-green-800/70 dark:text-green-200/70">
        Your prediction
      </p>

      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-green-900 dark:text-green-50">
        <span className="text-4xl sm:text-[2.75rem] font-bold tabular-nums leading-none tracking-tight">
          {prediction.homeGoals}
          <span className="mx-2 font-light opacity-30">-</span>
          {prediction.awayGoals}
        </span>
        {prediction.isBanker && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-black/10 dark:bg-black/20">
            Banker
          </span>
        )}
      </div>

      <div className="inline-flex items-center gap-2 mt-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-900/10 text-green-800 dark:bg-black/20 dark:text-green-100">
        <span className="uppercase tracking-wide">Locked in</span>
        <span className="opacity-35">·</span>
        <span>Awaiting result</span>
      </div>
    </div>
  </div>
);

export default ScoredMatchSummary;
